// 하드 난이도 진행 저장(dungeonhero.stage.hard.v1, D3b): 차례로 깬 하드 스테이지 수·지역별 고른 하드 스테이지·스테이지별 클리어/별·문 난이도 선택.
// 노말 진행(stage.v1)·save.v1·growth·gear 키는 바꾸지 않는다 — 새 키만 쓰므로 이전 빌드로 되돌려도 그 빌드는 이 키를 모르고 그대로 둔다.
// 해금 규칙은 shared/content/stages.ts(하드 n은 노말 n-10 클리어 + 하드 앞 지역 완료, 지역 안은 차례).
//
// 쓰기: 저장소 최신을 다시 읽어 합친다(깬 수·스테이지별 기록은 큰 쪽, 고른 스테이지·난이도 선택은 이 탭 값). 손상 원문은 .bak에 남기고 새로 시작한다
// (하드 진행은 이관할 이전 기록이 없다). 미래 버전(v > 1)은 읽기만 한다. 쓰기는 판 끝·마을 명령에서만(고정 스텝 프레임 루프 밖의 사건 단위).
import { REGION_IDS, type RegionId } from '../shared/levels';
import { STAGES_PER_REGION } from '../shared/content/world';
import {
  LAST_STAGE_ORDINAL, hardOrdinal, hardRegionFrontierIndex, hardRegionUnlocked, hardStageIdOf, hellOrdinal, hellRegionFrontierIndex, hellRegionUnlocked,
  hellStageIdOf, parseHardStage, parseHellStage,
} from '../shared/content/stages';
import type { HardStageId, HellStageId } from '../shared/content/types';
import type { HardProgressView } from '../shared/types';
import type { SaveStorage } from './save';
import { mergeOwed, parseOwedMap, type OwedReward } from './owedReward';

export const HARD_KEY = 'dungeonhero.stage.hard.v1';
export const HARD_BACKUP_KEY = HARD_KEY + '.bak';
export const HARD_BACKUP2_KEY = HARD_KEY + '.bak2';
export const HARD_VERSION = 1;
/**
 * 헬 난이도 진행(C10, dungeonhero.stage.hell.v1): 하드와 같은 모양·같은 규칙의 새 키. 이 저장소 클래스를 kind 'hell'로 만든다.
 * 하드·노말 키는 바꾸지 않으므로 이전 빌드로 되돌려도 헬 기록만 무시된다(그 빌드는 헬 키를 모른다).
 */
export const HELL_KEY = 'dungeonhero.stage.hell.v1';
export type DiffKind = 'hard' | 'hell';
const KEY_OF: Record<DiffKind, string> = { hard: HARD_KEY, hell: HELL_KEY };
const PARSE_OF: Record<DiffKind, (id: unknown) => { region: number; index: number } | null> = { hard: parseHardStage, hell: parseHellStage };
const ORD_OF: Record<DiffKind, (id: unknown) => number> = { hard: hardOrdinal, hell: hellOrdinal };

export interface HardSave {
  v: 1;
  /** 차례로 깬 하드 스테이지 수(0..LAST_STAGE_ORDINAL) */
  cleared: number;
  /** 지역별 고른 하드 스테이지 번호(1..10). 없으면 그 지역 하드 최전선 */
  selected: Partial<Record<RegionId, number>>;
  clears: Record<string, number>;
  stars: Record<string, number>;
  /**
   * 받은 하드 첫 클리어 보상(선택 필드, PR #175 리뷰): 하드 스테이지 id → 1. 없는 저장(이전 빌드)도 정상으로 읽고,
   * 깬 수(cleared) 이하의 하드 스테이지는 받은 것으로 친다(claimedFirst) — 이미 깬 하드 판을 다시 깨도 중복 지급이 없다
   */
  claimed: Record<string, 1>;
  /** 받는 중인 하드 첫 클리어 보상(선택 필드): 장비 장부·결과 영수증 저장이 확인되면 claimed로 바뀌며 지워진다(owedReward.ts) */
  owed: Record<string, OwedReward>;
  /** 마을 문 난이도 선택(true = 하드로 들어간다) */
  on: boolean;
}

type Read = { kind: 'none' } | { kind: 'ok'; data: HardSave } | { kind: 'corrupt'; raw: string } | { kind: 'future'; data: HardSave | null } | { kind: 'unavailable' };

function int(v: unknown, lo: number, hi: number, dflt: number): number {
  return typeof v === 'number' && Number.isInteger(v) && v >= lo && v <= hi ? v : dflt;
}
function obj(v: unknown): v is Record<string, unknown> { return v !== null && typeof v === 'object' && !Array.isArray(v); }

export function freshHard(cleared = 0): HardSave {
  return { v: 1, cleared: Math.max(0, Math.min(LAST_STAGE_ORDINAL, cleared)), selected: {}, clears: {}, stars: {}, claimed: {}, owed: {}, on: false };
}

/** 저장 원문 → 검증된 하드 진행(틀린 필드는 버리고, 필수 필드가 틀리면 null) */
export function parseHardSave(raw: unknown, kind: DiffKind = 'hard'): HardSave | null {
  const parseId = PARSE_OF[kind];
  if (!obj(raw) || raw.v !== 1) return null;
  const cleared = int(raw.cleared, 0, LAST_STAGE_ORDINAL, -1);
  if (cleared < 0) return null;
  const out = freshHard(cleared);
  if (obj(raw.selected)) for (const id of REGION_IDS) {
    const s = int(raw.selected[id], 1, STAGES_PER_REGION, 0);
    if (s > 0) out.selected[id] = s;
  }
  for (const key of ['clears', 'stars'] as const) {
    const src = raw[key];
    if (!obj(src)) continue;
    for (const [k, v] of Object.entries(src)) if (parseId(k) && int(v, 0, key === 'stars' ? 3 : 1e9, -1) >= 0) out[key][k] = v as number;
  }
  if (obj(raw.claimed)) for (const [k, v] of Object.entries(raw.claimed)) if (v === 1 && parseId(k)) out.claimed[k] = 1;
  out.owed = parseOwedMap(raw.owed, (k) => out.claimed[k] !== 1 && parseId(k) !== null);
  out.on = raw.on === true;
  return out;
}

/** 두 하드 진행 합치기: 깬 수·스테이지별 기록은 큰 쪽, 고른 스테이지·난이도 선택은 local 우선 */
export function mergeHard(base: HardSave, local: HardSave): HardSave {
  const out = freshHard(Math.max(base.cleared, local.cleared));
  out.selected = { ...base.selected, ...local.selected };
  for (const key of ['clears', 'stars'] as const) {
    for (const src of [base[key], local[key]]) for (const [k, v] of Object.entries(src)) out[key][k] = Math.max(out[key][k] ?? 0, v);
  }
  out.claimed = { ...base.claimed, ...local.claimed };
  out.owed = mergeOwed(base.owed, local.owed, out.claimed);
  out.on = local.on;
  return out;
}

export class HardProgressStore implements HardProgressView {
  private data: HardSave = freshHard();
  mode: 'ok' | 'future' = 'ok';
  revision = 0;
  lastSaveOk = true;
  recovered = 0;

  private readonly key: string;
  private readonly backupKey: string;
  private readonly backup2Key: string;
  /**
   * normalCleared: 노말 진행(stage.v1 차례로 깬 수) — 하드·헬 지역 해금 조건.
   * kind 'hell'이면 hardCleared(하드 진행 깬 수)도 해금 조건이다(헬 n = 노말 전부 + 하드 n-10 + 헬 앞 지역)
   */
  constructor(private readonly storage: SaveStorage, private readonly normalCleared: () => number, readonly kind: DiffKind = 'hard', private readonly hardCleared: () => number = () => 0) {
    this.key = KEY_OF[kind]; this.backupKey = this.key + '.bak'; this.backup2Key = this.key + '.bak2';
    this.load();
  }
  private parseId(id: unknown): { region: number; index: number } | null { return PARSE_OF[this.kind](id); }
  private ordinal(id: unknown): number { return ORD_OF[this.kind](id); }
  private regionOk(own: number, region: number): boolean {
    return this.kind === 'hell' ? hellRegionUnlocked(this.normalCleared(), this.hardCleared(), own, region) : hardRegionUnlocked(this.normalCleared(), own, region);
  }
  private frontier(own: number, region: number): number {
    return this.kind === 'hell' ? hellRegionFrontierIndex(this.normalCleared(), this.hardCleared(), own, region) : hardRegionFrontierIndex(this.normalCleared(), own, region);
  }
  private idOf(region: number, index: number): HardStageId | HellStageId {
    return this.kind === 'hell' ? hellStageIdOf(region, index) : hardStageIdOf(region, index);
  }

  get cleared(): number { return this.data.cleared; }
  /** 마을 문 난이도가 하드인지(하드 지역이 하나라도 열렸을 때만 의미가 있다) */
  get on(): boolean { return this.data.on; }
  /** 하드 1지역이 열렸는지(노말 1-10 클리어) — 문 앞 카드 난이도 버튼을 보일지 */
  get available(): boolean { return this.regionOk(0, 1); }
  /**
   * 이 하드 스테이지의 첫 클리어 보상을 이미 받았거나 받는 중인가. 깬 수 이하(이전 빌드에서 이미 깬 하드 판)도 받은 것으로 친다 —
   * 이전 빌드가 claimed를 지우고 다시 써도 중복 지급이 없다
   */
  claimedFirst(id: string): boolean {
    return this.data.claimed[id] === 1 || !!this.data.owed[id] || this.ordinal(id) <= this.data.cleared;
  }
  /** 첫 클리어 보상을 줘야 하는 판인가(미리 판정, 기록하지 않는다 — flow가 골드를 결과에 합산할 때 쓴다) */
  firstRewardDue(id: string): boolean {
    this.refresh();
    return this.parseId(id) !== null && this.unlocked(id) && !this.claimedFirst(id);
  }
  /** 받는 중인 하드 첫 클리어 보상이 있는가(저장 확인 대기, 틱마다 불러도 할당이 없다) */
  get hasOwed(): boolean { for (const k in this.data.owed) if (this.data.owed[k]) return true; return false; }
  owedEntries(): [string, OwedReward][] { return Object.entries(this.data.owed); }
  /** 받는 중인 보상을 수령으로 확정한다(호출자가 장비 장부·결과 영수증 저장을 확인한 뒤). 영속됐으면 true, 아니면 되돌리고 false */
  settleOwed(key: string): boolean {
    const prev = this.data.owed[key];
    if (!prev) return true;
    this.refresh();
    this.data.claimed[key] = 1;
    delete this.data.owed[key];
    this.write();
    if (this.lastSaveOk) return true;
    // 저장 실패(쓰기 오류·읽기 전용): 메모리도 받는 중으로 되돌려 다음 주기에 다시 시도한다(호출자는 이 동안 골드를 주지 않는다)
    delete this.data.claimed[key];
    this.data.owed[key] = prev;
    return false;
  }
  /** 하드 4-10까지 다 깼는지 */
  get complete(): boolean { return this.data.cleared >= LAST_STAGE_ORDINAL; }
  stageClears(id: string): number { return this.data.clears[id] ?? 0; }
  stageStars(id: string): number { return this.data.stars[id] ?? 0; }
  unlocked(id: string): boolean {
    const p = this.parseId(id);
    return !!p && this.regionOk(this.data.cleared, p.region) && this.ordinal(id) <= this.data.cleared + 1;
  }
  regionUnlocked(region: RegionId): boolean { return this.regionOk(this.data.cleared, REGION_IDS.indexOf(region) + 1); }
  /** 그 지역에서 들어갈 하드 스테이지(고른 것이 열려 있으면 그것, 아니면 하드 최전선). 잠겼으면 null */
  selected(region: RegionId): HardStageId | HellStageId | null {
    const no = REGION_IDS.indexOf(region) + 1;
    const max = this.frontier(this.data.cleared, no);
    if (max <= 0) return null;
    const s = this.data.selected[region];
    return this.idOf(no, s && s <= max ? s : max);
  }

  private read(): Read {
    let raw: string | null;
    try { raw = this.storage.getItem(this.key); } catch { return { kind: 'unavailable' }; }
    if (raw === null) return { kind: 'none' };
    let parsed: unknown;
    try { parsed = JSON.parse(raw); } catch { return { kind: 'corrupt', raw }; }
    if (obj(parsed) && typeof parsed.v === 'number' && parsed.v > HARD_VERSION) return { kind: 'future', data: parseHardSave({ ...parsed, v: 1 }, this.kind) };
    const data = parseHardSave(parsed, this.kind);
    return data ? { kind: 'ok', data } : { kind: 'corrupt', raw };
  }

  /** 처음 읽기: 없으면 아무것도 쓰지 않는다(하드를 처음 깨거나 난이도를 고를 때 키가 생긴다 — 하드를 안 쓰는 계정은 새 키도 없다) */
  private load(): void {
    const r = this.read();
    if (r.kind === 'ok') { this.data = r.data; return; }
    if (r.kind === 'future') { this.mode = 'future'; if (r.data) this.data = r.data; return; }
    // 손상: 새로 시작해 바로 쓴다(write가 덮어쓰기 전에 원문을 .bak에 남긴다)
    if (r.kind === 'corrupt') { this.data = freshHard(); this.write(); }
  }

  /** 손상 원문 백업: 첫 손상은 .bak(덮어쓰지 않음), 그 뒤 다른 손상은 .bak2에 최신만(노말 진행 저장과 같은 규칙) */
  private backup(raw: string): void {
    try {
      const first = this.storage.getItem(this.backupKey);
      if (first === null) this.storage.setItem(this.backupKey, raw);
      else if (first !== raw) this.storage.setItem(this.backup2Key, raw);
    } catch { /* 백업 실패해도 진행은 메모리 값으로 이어 쓴다 */ }
    this.recovered++;
  }

  /**
   * 다른 탭이 쓴 하드 진행을 읽어 합친다(맵 이동·창 포커스마다, 사건 단위). 화면에 보이는 값(깬 수·스테이지 기록·별)이
   * 바뀌었을 때만 revision을 올린다 — 기록만 바뀐 경우도 카드가 다시 그리게(PR #176 리뷰 P2)
   */
  refresh(): void {
    const r = this.read();
    if (r.kind !== 'ok' && r.kind !== 'future') return;
    if (r.kind === 'future') this.mode = 'future';
    if (!r.data) return;
    const before = JSON.stringify(this.data);
    this.data = mergeHard(r.data, this.data);
    if (JSON.stringify(this.data) !== before) this.revision++;
  }

  /** 실패한 쓰기 다시 하기(결과 화면 재시도). 저장됐으면 true */
  retrySave(): boolean {
    this.write();
    return this.lastSaveOk;
  }

  private write(): void {
    if (this.mode !== 'ok') { this.lastSaveOk = false; return; }
    const r = this.read();
    if (r.kind === 'ok') this.data = mergeHard(r.data, this.data);
    else if (r.kind === 'future') { this.mode = 'future'; this.lastSaveOk = false; return; }
    else if (r.kind === 'corrupt') this.backup(r.raw); // 생성 뒤에 손상된 원문도 덮어쓰기 전에 남긴다(PR #176 리뷰 P2)
    try {
      const text = JSON.stringify(this.data);
      this.storage.setItem(this.key, text);
      this.lastSaveOk = this.storage.getItem(this.key) === text;
    } catch { this.lastSaveOk = false; }
    this.revision++;
  }

  /** 하드 스테이지 클리어 기록. 차례대로 다음 하드 스테이지를 처음 깨면 진행이 오른다. 결과: first(첫 클리어) */
  recordClear(id: HardStageId | HellStageId, stars: number, owed?: OwedReward): { first: boolean; rewardDue: boolean } {
    const p = this.parseId(id);
    if (!p) return { first: false, rewardDue: false };
    this.refresh();
    const o = this.ordinal(id);
    const first = o === this.data.cleared + 1 && this.unlocked(id);
    // 첫 클리어 보상(스테이지별 1회): 보상 내용이 딸려 오면 받는 중(owed)으로 적고 수령(claimed)은 settleOwed가 저장 확인 뒤에 쓴다
    const rewardDue = this.unlocked(id) && !this.claimedFirst(id);
    if (rewardDue) { if (owed) this.data.owed[id] = owed; else this.data.claimed[id] = 1; }
    if (first) this.data.cleared = o;
    this.data.clears[id] = (this.data.clears[id] ?? 0) + 1;
    this.data.stars[id] = Math.max(this.data.stars[id] ?? 0, Math.max(0, Math.min(3, Math.floor(stars))));
    if (first && p.index < STAGES_PER_REGION) this.data.selected[REGION_IDS[p.region - 1]] = p.index + 1;
    this.write();
    return { first, rewardDue };
  }

  /** 지역의 하드 스테이지 고르기(열린 것만). 성공하면 true */
  select(region: RegionId, index: number): boolean {
    const no = REGION_IDS.indexOf(region) + 1;
    if (no < 1 || !Number.isInteger(index) || index < 1 || index > this.frontier(this.data.cleared, no)) return false;
    if (this.data.selected[region] === index) return true;
    this.data.selected[region] = index;
    this.write();
    return true;
  }

  /** 마을 문 난이도 고르기. 하드는 하드 1지역이 열렸을 때만. 성공하면 true */
  setOn(on: boolean): boolean {
    if (on && !this.available) return false;
    if (this.data.on === on) return true;
    this.data.on = on;
    this.write();
    return true;
  }

  /** 시험·QA: 저장 원문 */
  snapshot(): HardSave { return JSON.parse(JSON.stringify(this.data)) as HardSave; }
}

/** 시험·QA 픽스처: 하드 진행 원문(하드 서수 cleared까지 깬 상태) */
export function hardFixture(cleared: number, extra: Partial<HardSave> = {}): string {
  return JSON.stringify({ ...freshHard(cleared), ...extra });
}
